import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import { makeStyles } from 'tss-react/mui';

import IconButton from '@mui/material/IconButton';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import MenuItem from '@mui/material/MenuItem';
import MenuList from '@mui/material/MenuList';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';

import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';

const useStyles = makeStyles()((theme) => ({
  root: {
    display: 'inline-flex',
    alignItems: 'center',
  },
  iconButton: {
    padding: 0,
  },
  icon: {
    fontSize: 23,
  },
  popper: {
    zIndex: theme.zIndex.modal,
  },
  menuList: {
    padding: theme.spacing(0.5),
  },
  menuItem: {
    fontSize: 14,
    minHeight: 28,
  },
}));

interface CheckOptionPickerProps {
  optionLabels?: string[];
  onSelect?: (selectedLabel: string) => void;
  className?: string;
  icon?: React.ReactNode;
  disabled?: boolean;
}

const STATIC_CHECK_LABELS: string[] = ['QueuePmt'];

const CheckOptionPicker: FC<CheckOptionPickerProps> = ({
  optionLabels = STATIC_CHECK_LABELS,
  onSelect,
  className,
  icon,
  disabled = false,
}) => {
  const { classes, cx } = useStyles();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [open, setOpen] = useState(false);
  const closeTimerRef = useRef<number | null>(null);

  const clearCloseTimer = useCallback(() => {
    if (closeTimerRef.current) {
      window.clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }, []);

  const handleOpen = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      if (disabled) return;
      clearCloseTimer();
      setAnchorEl(event.currentTarget);
      setOpen(true);
    },
    [clearCloseTimer, disabled],
  );

  const handleToggle = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      if (disabled) return;
      clearCloseTimer();
      setAnchorEl(event.currentTarget);
      setOpen((prev) => !prev);
    },
    [clearCloseTimer, disabled],
  );

  const scheduleClose = useCallback(() => {
    clearCloseTimer();
    closeTimerRef.current = window.setTimeout(() => {
      setOpen(false);
    }, 120);
  }, [clearCloseTimer]);

  const handleSelect = useCallback(
    (selectedLabel: string) => {
      onSelect?.(selectedLabel);
      setOpen(false);
    },
    [onSelect],
  );

  useEffect(() => () => clearCloseTimer(), [clearCloseTimer]);

  return (
    <span className={cx(classes.root, className)}>
      <IconButton
        size="small"
        className={classes.iconButton}
        onMouseEnter={handleOpen}
        onMouseLeave={scheduleClose}
        onClick={handleToggle}
        onMouseDown={(event) => event.preventDefault()}
        aria-label="Check options"
        aria-haspopup="menu"
        disabled={disabled}
      >
        {icon || <ExpandMoreRoundedIcon className={classes.icon} />}
      </IconButton>
      <Popper
        open={open}
        anchorEl={anchorEl}
        placement="bottom-end"
        className={classes.popper}
      >
        <ClickAwayListener onClickAway={() => setOpen(false)}>
          <Paper onMouseEnter={clearCloseTimer} onMouseLeave={scheduleClose}>
            <MenuList className={classes.menuList}>
              {optionLabels.map((optionLabel) => (
                <MenuItem
                  key={optionLabel}
                  className={classes.menuItem}
                  onClick={() => handleSelect(optionLabel)}
                >
                  {optionLabel}
                </MenuItem>
              ))}
            </MenuList>
          </Paper>
        </ClickAwayListener>
      </Popper>
    </span>
  );
};

export default CheckOptionPicker;
